import { Analytics } from '@boost/widget-integration-tae/lib';
import { Injectable } from 'framework/di';
import { pipe } from 'framework/functional';

import { ShopifyMarketHelper } from '../../helpers/shopify-market.helper';
import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';
import type { PredictiveBundleData } from './predictive-bundle.model';

interface BundleQueryParams extends Record<string, string | number | boolean | undefined> {
  product_id: number;
  shop: string;
  country?: string;
  customer_id?: string | number;
  locale?: string;
}

interface BundleResponse {
  bundles: PredictiveBundleData[];
  meta?: {
    rid?: string;
    money_format?: string;
    money_format_with_currency?: string;
    currency?: string;
  };
}

/**
 * Fetches predictive bundle data and applies shop-specific request enrichment.
 *
 * Override request-building methods to add custom segmentation, localization,
 * or analytics parameters for Technical Support customizations.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('PredictiveBundleAPI', (PredictiveBundleAPI) => {
 *   return class extends PredictiveBundleAPI {
 *     buildBundleQueryParams(params) {
 *       const enhanced = super.buildBundleQueryParams(params);
 *       return { ...enhanced, campaign_id: window.currentCampaignId };
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class PredictiveBundleAPI extends BoostAPI {
  constructor(
    protected platformLoader: PlatformLoader,
    protected appService: AppService,
    protected b2bService: B2BService,
    protected shopifyMarketHelper: ShopifyMarketHelper
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig.bundleUrl || '';
  }

  /**
   * Builds the request parameters sent to the predictive bundle endpoint.
   *
   * @param params - Base query params derived from the current storefront context.
   * @returns Enriched query params with currency, B2B, and localization information.
   */
  protected buildBundleQueryParams(params: BundleQueryParams): BundleQueryParams {
    return pipe(
      { ...params },
      (p) => this.addCurrencyParams(p),
      (p) => this.b2bService.addB2BRequestParams(p),
      (p) => this.addLocalizationParams(p)
    );
  }

  /**
   * Loads predictive bundles for a product.
   *
   * @param productId - Shopify product id used to query bundle recommendations.
   * @returns Predictive bundles for the product, or an empty array when unavailable.
   */
  async getPredictiveBundles(productId: number): Promise<PredictiveBundleData[]> {
    if (!productId) return [];

    const params: BundleQueryParams = {
      product_id: productId,
      shop: this.appService.TAEAppConfig.shop?.domain || '',
      country: this.platformLoader.platform.localization?.country || '',
      customer_id: this.platformLoader.platform.customer?.id,
    };

    const enhancedParams = this.buildBundleQueryParams(params);
    const filteredParams: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(enhancedParams)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
    }
    const queryParams = this.buildDefaultQueryParams(filteredParams);

    try {
      const response = await this.get<BundleResponse>(`?${queryParams}`);
      if (response.status === 200 && response.data) {
        if (response.data.meta) {
          this.shopifyMarketHelper.restrictMainCurrency({
            money_format: response.data.meta.money_format || '',
            money_format_with_currency: response.data.meta.money_format_with_currency || '',
            currency: response.data.meta.currency || '',
          });

          this.appService
            .getTAEModule<Analytics>('Analytics')
            ?.saveRequestId(
              'product_page_bundle',
              response.data.meta.rid || '',
              response.data?.bundles
            );
        }
        return response.data.bundles || [];
      }
      return [];
    } catch {
      return [];
    }
  }
}
